import React, { useEffect, useRef, Suspense, createContext, useContext, useCallback, useState, Component, ErrorInfo } from 'react';
import { Canvas, ThreeEvent, useThree, useFrame } from '@react-three/fiber';
import { OrbitControls, Grid, OrthographicCamera } from '@react-three/drei';
import LidarSensor from './LidarSensor';
import Obstacle from './Obstacle';
import { CameraMovingProvider } from './useCameraMoving';
import HeatmapPlane from './HeatmapPlane';
import ReferenceImagePlane from './ReferenceImagePlane';
import ReferenceImageDownscalePrompt from './ReferenceImageDownscalePrompt';
import WallDrawingOverlay from './WallDrawingOverlay';
import DraggableAreaOfInterest from './DraggableAreaOfInterest';
import { SceneState, useSceneStore, ColorScale } from '../../state/sceneStore';
import { Obstacle as ObstacleType } from '../../api/models';
import { updateProject } from '../../api/client';
import { useDesignPointClouds } from '../../hooks/useDesignPointClouds';
import SensorPointCloud from './SensorPointCloud';
import PointCloudLoadStatus from './PointCloudLoadStatus';

interface ReferenceImageErrorBoundaryProps {
  imageUrl: string;
  children: React.ReactNode;
}

interface ReferenceImageErrorBoundaryState {
  hasError: boolean;
}

class ReferenceImageErrorBoundary extends Component<ReferenceImageErrorBoundaryProps, ReferenceImageErrorBoundaryState> {
  constructor(props: ReferenceImageErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ReferenceImageErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Reference image failed to load:', error.message);
    console.warn(
      'The project\'s reference image could not be loaded. ' +
      'The image file may have been deleted or is inaccessible. ' +
      'You can re-upload a reference image from the Properties panel.'
    );
  }

  componentDidUpdate(prevProps: ReferenceImageErrorBoundaryProps) {
    if (prevProps.imageUrl !== this.props.imageUrl && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return null;
    }
    return this.props.children;
  }
}

// Z Scale Context for preventing z-fighting in orthographic view
const ZScaleContext = createContext<number>(1);

export const useZScale = () => useContext(ZScaleContext);


// ZScaleProvider component that calculates dynamic z-scale based on camera
const ZScaleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { camera } = useThree();
  const useOrthographic = useSceneStore((state: SceneState) => state.useOrthographic);
  const [zScale, setZScale] = useState<number>(useOrthographic ? 2 : 1);

  useFrame(() => {
    if (useOrthographic) {
      if (zScale !== 2) {
        setZScale(2);
      }
      return;
    }
    const distance = Math.abs(camera.position.z);
    const next = Math.max(.1, distance * 0.2);
    if (Math.abs(next - zScale) > 0.01) {
      setZScale(next);
    }
  });

  return (
    <ZScaleContext.Provider value={zScale}>
      {children}
    </ZScaleContext.Provider>
  );
};

// Grid layer that respects dynamic zScale
const GridLayer: React.FC<{ areaOfInterest: { min_x: number; max_x: number; min_y: number; max_y: number }; cellSize: number; }> = ({ areaOfInterest, cellSize }) => {
  const zScale = useZScale();
  const gridZ = 0.05 * zScale; // Above reference image and heatmap

  return (
    <Grid
      rotation-x={Math.PI / 2}
      position={[
        (areaOfInterest.min_x + areaOfInterest.max_x) / 2,
        (areaOfInterest.min_y + areaOfInterest.max_y) / 2,
        gridZ,
      ]}
      args={[areaOfInterest.max_x - areaOfInterest.min_x, areaOfInterest.max_y - areaOfInterest.min_y]}
      cellSize={cellSize}
      cellColor={'#6f6f6f'}
      sectionSize={cellSize * 5}
      sectionColor={'#9d9d9d'}
      fadeDistance={150}
    />
  );
};

// Heatmap layer that respects dynamic zScale
const HeatmapLayer: React.FC<{ visible: boolean; heatmapData: any; areaOfInterest: { min_x: number; max_x: number; min_y: number; max_y: number }; heatmapScale: { min: number; max: number }; colorScale: ColorScale; }> = ({ visible, heatmapData, areaOfInterest, heatmapScale, colorScale }) => {
  const zScale = useZScale();
  const heatmapZ = 0.04 * zScale;

  if (!visible || !heatmapData) return null;
  return (
    <HeatmapPlane
      heatmapData={heatmapData}
      heatmapScale={heatmapScale}
      colorScale={colorScale}
      position={[
        (areaOfInterest.min_x + areaOfInterest.max_x) / 2,
        (areaOfInterest.min_y + areaOfInterest.max_y) / 2,
        heatmapZ,
      ]}
      size={[areaOfInterest.max_x - areaOfInterest.min_x, areaOfInterest.max_y - areaOfInterest.min_y]}
    />
  );
};

// Bridge component that registers the WebGL canvas element and active camera
// in the scene store so non-R3F components (e.g. SimulationPanel) can capture
// the current scene for image export.
const SceneRefBridge: React.FC = () => {
  const { gl, camera } = useThree();
  const setSceneRefs = useSceneStore((state: SceneState) => state.setSceneRefs);

  useEffect(() => {
    setSceneRefs(gl.domElement, camera);
    return () => setSceneRefs(null, null);
  }, [gl, camera, setSceneRefs]);

  return null;
};

// Camera component to handle orthographic projection
const CameraSetup: React.FC = () => {
  const { size, camera } = useThree();
  const areaOfInterest = useSceneStore((state: SceneState) => state.sceneOptions.areaOfInterest);
  const useOrthographic = useSceneStore((state: SceneState) => state.useOrthographic);
  const storedOrthographicZoom = useSceneStore((state: SceneState) => state.orthographicZoom);
  const setOrthographicZoom = useSceneStore((state: SceneState) => state.setOrthographicZoom);

  const width = areaOfInterest.max_x - areaOfInterest.min_x;
  const height = areaOfInterest.max_y - areaOfInterest.min_y;
  const maxDimension = Math.max(width, height);
  const computedZoom = Math.min(size.width, size.height) / (maxDimension * 2.5); // Add padding for better overview of large scenes

  // Initialize zoom only when entering orthographic mode
  const prevUseOrthographic = React.useRef(useOrthographic);
  const initialZoom = React.useMemo(() => {
    // Use stored zoom if available, otherwise compute initial zoom
    if (storedOrthographicZoom !== null && storedOrthographicZoom > 0) {
      return storedOrthographicZoom;
    }
    return isFinite(computedZoom) && computedZoom > 0 ? computedZoom : 1;
  }, [storedOrthographicZoom, computedZoom]);

  // Track zoom changes and persist them
  useFrame(() => {
    if (useOrthographic && camera && 'zoom' in camera) {
      const currentZoom = (camera as any).zoom;
      if (currentZoom !== storedOrthographicZoom && isFinite(currentZoom) && currentZoom > 0) {
        setOrthographicZoom(currentZoom);
      }
    }
  });

  React.useEffect(() => {
    prevUseOrthographic.current = useOrthographic;
  }, [useOrthographic]);

  if (!useOrthographic) {
    return null; // Use default perspective camera
  }

  return (
    <OrthographicCamera
      makeDefault
      position={[0, 0, 20]}
      zoom={initialZoom}
      near={0.1}
      far={100000}
      left={-size.width / 2 / initialZoom}
      right={size.width / 2 / initialZoom}
      top={size.height / 2 / initialZoom}
      bottom={-size.height / 2 / initialZoom}
    />
  );
};

const SceneCanvas: React.FC = () => {
  const sensors = useSceneStore((state: SceneState) => state.sensors);
  const obstacles = useSceneStore((state: SceneState) => state.obstacles);
  const isDragging = useSceneStore((state: SceneState) => state.isDragging);
  const heatmapData = useSceneStore((state: SceneState) => state.heatmapData);
  const heatmapVisible = useSceneStore((state: SceneState) => state.heatmapVisible);
  const pointCloudVisible = useSceneStore((state: SceneState) => state.pointCloudVisible);
  const obstaclesVisible = useSceneStore((state: SceneState) => state.obstaclesVisible);
  const sceneOptions = useSceneStore((state: SceneState) => state.sceneOptions);
  const { areaOfInterest } = sceneOptions;
  const currentProject = useSceneStore((state: SceneState) => state.currentProject);
  const isWallDrawingMode = useSceneStore((state: SceneState) => state.isWallDrawingMode);
  const selectedObjectId = useSceneStore((state: SceneState) => state.selectedObjectId);
  const deleteSensor = useSceneStore((state: SceneState) => state.deleteSensor);
  const deleteObstacle = useSceneStore((state: SceneState) => state.deleteObstacle);
  const isReadOnly = useSceneStore((state: SceneState) => state.isReadOnly);
  const isAlignmentMode = useSceneStore((state: SceneState) => state.isAlignmentMode);

  const {
    clouds: designPointClouds,
    demoPoints,
  } = useDesignPointClouds({
    sensors,
    obstacles,
    pointCloudVisible,
    isAlignmentMode,
    isReadOnly,
    isDragging,
    projectId: currentProject?.id ?? null,
  });

  const orbitControlsRef = useRef<any>(null);
  const isDraggingRef = useRef(false);

  // Sync dragging state with ref for immediate OrbitControls control
  useEffect(() => {
    isDraggingRef.current = isDragging;
    if (orbitControlsRef.current) {
      orbitControlsRef.current.enabled = !isDragging;
    }
  }, [isDragging]);

  const referenceImageVisible = useSceneStore((state: SceneState) => state.referenceImageVisible);
  const [referenceImageDimensions, setReferenceImageDimensions] = useState<{ width: number; height: number } | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastRightClickTime = useRef<number>(0);
  const addSensor = useSceneStore((state: SceneState) => state.addSensor);
  const addObstacle = useSceneStore((state: SceneState) => state.addObstacle);
  const selectedSensorType = useSceneStore((state: SceneState) => state.selectedSensorType);
  const useOrthographic = useSceneStore((state: SceneState) => state.useOrthographic);
  const setSceneOptions = useSceneStore((state: SceneState) => state.setSceneOptions);

  // Z scale is handled by ZScaleProvider

  // Function to auto-resize area of interest based on reference image
  const autoResizeAreaOfInterest = useCallback((imageUrl: string) => {
    const img = new Image();
    img.onload = () => {
      // Calculate aspect ratio and scale to reasonable size
      const aspectRatio = img.width / img.height;
      const maxDimension = 200; // Maximum dimension in world units

      let width, height;
      if (aspectRatio > 1) {
        width = maxDimension;
        height = maxDimension / aspectRatio;
      } else {
        height = maxDimension;
        width = maxDimension * aspectRatio;
      }

      const centerX = 0;
      const centerY = 0;

      setSceneOptions({
        areaOfInterest: {
          min_x: centerX - width / 2,
          max_x: centerX + width / 2,
          min_y: centerY - height / 2,
          max_y: centerY + height / 2,
        }
      });
    };
    img.onerror = () => {
      console.warn('Failed to load reference image:', imageUrl);
      // Set default area of interest when image fails to load
      setSceneOptions({
        areaOfInterest: {
          min_x: -20,
          max_x: 20,
          min_y: -20,
          max_y: 20,
        }
      });
    };
    img.src = imageUrl;
  }, [setSceneOptions]);


  useEffect(() => {
    if (currentProject && !useSceneStore.getState().isReadOnly) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        updateProject(currentProject);
      }, 1000); // 1 second debounce
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [currentProject]);

  // Auto-resize area of interest when reference image changes
  useEffect(() => {
    if (currentProject?.reference_image_url) {
      // Only auto-resize if the project doesn't have saved simulation options
      // or if the AOI values are still at default values
      const hasDefaultAOI = !currentProject.simulation_options?.areaOfInterest ||
        (currentProject.simulation_options.areaOfInterest.min_x === -100 &&
          currentProject.simulation_options.areaOfInterest.max_x === 100 &&
          currentProject.simulation_options.areaOfInterest.min_y === -100 &&
          currentProject.simulation_options.areaOfInterest.max_y === 100);

      if (hasDefaultAOI) {
        autoResizeAreaOfInterest(currentProject.reference_image_url);
      }
    }
  }, [currentProject?.reference_image_url, autoResizeAreaOfInterest, currentProject?.simulation_options]);

  useEffect(() => {
    setReferenceImageDimensions(null);
  }, [currentProject?.reference_image_url]);

  const handleReferenceImageDimensionsLoaded = useCallback((width: number, height: number) => {
    setReferenceImageDimensions((current) => {
      if (current?.width === width && current?.height === height) {
        return current;
      }
      return { width, height };
    });
  }, []);

  // Handle delete key for selected objects in wall drawing mode
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Delete' && selectedObjectId) {
        // Prevent deletion in read-only mode, but allow in normal mode (including wall drawing if meaningful, though wall drawing used to require isWallDrawingMode check here, we'll respect existing logic but add read-only check)

        const isReadOnly = useSceneStore.getState().isReadOnly;
        const isAlignmentMode = useSceneStore.getState().isAlignmentMode;
        if (isReadOnly || isAlignmentMode) return;

        // Find if it's a sensor or obstacle
        const sensor = sensors.find(s => s.id === selectedObjectId);
        const obstacle = obstacles.find(o => o.id === selectedObjectId);

        if (sensor) {
          deleteSensor(selectedObjectId);
        } else if (obstacle) {
          deleteObstacle(selectedObjectId);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedObjectId, isWallDrawingMode, sensors, obstacles, deleteSensor, deleteObstacle]);

  const handleDoubleClick = (event: ThreeEvent<MouseEvent>) => {
    // Don't add sensors when in wall drawing mode or read-only mode or alignment mode
    if (isWallDrawingMode || useSceneStore.getState().isReadOnly || useSceneStore.getState().isReferenceImageAlignMode || useSceneStore.getState().isAlignmentMode) {
      return;
    }

    if (event.button === 0) { // Left double-click
      const intersection = event.intersections.find(i => i.object.name === 'ground');
      if (intersection) {
        const { x, y } = intersection.point;
        addSensor(selectedSensorType, [x, y]);
      }
    }
  };

  const handleContextMenu = (event: ThreeEvent<MouseEvent>) => {
    event.stopPropagation();
    // Ignore context menu actions while in wall drawing mode to avoid conflicts
    // Also ignore in read-only mode or alignment mode
    if (isWallDrawingMode || useSceneStore.getState().isReadOnly || useSceneStore.getState().isReferenceImageAlignMode || useSceneStore.getState().isAlignmentMode) {
      return;
    }
    const now = Date.now();
    if (now - lastRightClickTime.current < 300) { // Double right-click
      const intersection = event.intersections.find(i => i.object.name === 'ground');
      if (intersection) {
        const { x, y } = intersection.point;
        addObstacle([x, y, 0.5]);
      }
    }
    lastRightClickTime.current = now;
  };

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <Canvas
        gl={{ preserveDrawingBuffer: true }}
        camera={{
          position: isReadOnly ? [-80, -40, 60] : [0, -10, 10],
          fov: isReadOnly ? 45 : 50,
          up: [0, 0, 1],
          far: 100000
        }}
      >
        <SceneRefBridge />
        <CameraSetup />
        <ambientLight intensity={0.5} />
        <directionalLight position={[10, 10, 5]} intensity={1} />
        <OrbitControls
          ref={orbitControlsRef}
          enabled={!isDragging}
          enableRotate={!useOrthographic && !isDragging} // Disable rotation in orthographic mode and during dragging
          enableZoom={!isDragging} // Disable zoom during dragging
          enablePan={!isDragging} // Disable pan during dragging
          minPolarAngle={useOrthographic ? Math.PI / 2 : 0} // Pin to top-down when orthographic
          maxPolarAngle={Math.PI / 2}
          target={useOrthographic ? [0, 0, 0] : (isReadOnly ? [-40, 20, 0] : [0, 0, 0])}
        />
        <CameraMovingProvider controlsRef={orbitControlsRef}>
        {/* Infinite ground plane - z = 0 */}
        <mesh
          position={[0, 0, 0]}
          name="ground"
          visible={true}
          onDoubleClick={handleDoubleClick}
          onContextMenu={handleContextMenu}
        >
          <planeGeometry args={[10000, 10000]} />
          <meshStandardMaterial color="#f0f0f0" />
        </mesh>

        {/* Draggable Area of Interest */}
        <DraggableAreaOfInterest />

        <ZScaleProvider>
          {/* Reference image plane - z = 0.02 * zScale */}
          {currentProject && currentProject.reference_image_url && referenceImageVisible && (
            <ReferenceImageErrorBoundary imageUrl={currentProject.reference_image_url}>
              <Suspense fallback={null}>
                <ReferenceImagePlane
                  imageUrl={currentProject.reference_image_url}
                  onDimensionsLoaded={handleReferenceImageDimensionsLoaded}
                />
              </Suspense>
            </ReferenceImageErrorBoundary>
          )}

          {/* Wall drawing overlay - only show when in wall drawing mode */}
          {currentProject && currentProject.reference_image_url && referenceImageVisible && (
            <ReferenceImageErrorBoundary imageUrl={currentProject.reference_image_url}>
              <WallDrawingOverlay imageUrl={currentProject.reference_image_url} />
            </ReferenceImageErrorBoundary>
          )}

          {/* Heatmap layer - z = 0.05 * zScale */}
          <HeatmapLayer
            visible={Boolean(heatmapData) && heatmapVisible}
            heatmapData={heatmapData}
            areaOfInterest={areaOfInterest}
            heatmapScale={sceneOptions.heatmapScale}
            colorScale={sceneOptions.colorScale}
          />

          {/* Grid layer - z = 0.05 * zScale */}
          <GridLayer areaOfInterest={areaOfInterest} cellSize={sceneOptions.gridCellSize} />

          {/* Simulation point cloud — disabled in alignment mode (aligned scans render per-sensor). */}
          {pointCloudVisible && !isAlignmentMode && (
            <>
              {demoPoints ? (
                <SensorPointCloud points={demoPoints} />
              ) : (
                designPointClouds.map((cloud) => (
                  <SensorPointCloud
                    key={cloud.sensorId}
                    points={cloud.points}
                  />
                ))
              )}
            </>
          )}
        </ZScaleProvider>

        {/* Sensors and obstacles - fixed z positions */}
        <LidarSensor />
        {obstaclesVisible && obstacles.map((obstacle: ObstacleType) => (
          <Obstacle key={obstacle.id} id={obstacle.id} />
        ))}
        </CameraMovingProvider>
      </Canvas>

      <PointCloudLoadStatus />

      {referenceImageDimensions && (
        <ReferenceImageDownscalePrompt
          width={referenceImageDimensions.width}
          height={referenceImageDimensions.height}
        />
      )}

    </div>
  );
};

export default SceneCanvas;
